import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IBid, IEvent, IProgram, IRaceState, IRobot, IStage } from "./interfaces";

const initialState: IRaceState = {
  status: null,
  robots: [],
  robotBidMap: {},
  raceTimeLimit: 0,
  time: 0,
  isAdmin: localStorage.getItem("uuuuuu") === "-544719056",
  events: [],
  selectedEventId: null,
  programs: [],
  selectedProgramId: null,
  bids: [],
  stages: [],
  selectedStageId: null,
  pendingLapsQueue: {},
};

const receSlice = createSlice({
  name: "race",
  initialState,
  reducers: {
    setRaceStatus: (state, action: PayloadAction<IRaceState["status"]>) => {
      state.status = action.payload;
    },

    raceTime: (state, action: PayloadAction<number>) => {
      state.time = action.payload;
    },

    raceTimeLimit: (state, action: PayloadAction<number>) => {
      state.raceTimeLimit = action.payload;
    },

    raceRobot: (state, action: PayloadAction<IRobot>) => {
      const robot = action.payload;
      const robots = state.robots;
      const indexRobot = robots.findIndex((r) => r.serial === robot.serial);
      if (indexRobot === -1) {
        robots.push(robot);
      } else {
        robots[indexRobot] = robot;
      }
      // Remove first pending delta when server confirms (FIFO)
      const queue = state.pendingLapsQueue[robot.serial];
      if (queue && queue.length > 0) {
        queue.shift();
        if (queue.length === 0) {
          delete state.pendingLapsQueue[robot.serial];
        }
      }
    },

    addPendingLaps: (state, action: PayloadAction<{ serial: number; delta: number; time: number }>) => {
      const { serial, delta, time } = action.payload;
      if (!state.pendingLapsQueue[serial]) {
        state.pendingLapsQueue[serial] = [];
      }
      state.pendingLapsQueue[serial].push({ delta, time, addedAt: Date.now() });
    },

    clearPendingLaps: (state, action: PayloadAction<number>) => {
      delete state.pendingLapsQueue[action.payload];
    },

    removeRobot: (state, action: PayloadAction<IRobot["serial"]>) => {
      const robots = state.robots;
      const removingIndexRobot = robots.findIndex(
        (r) => r.serial === action.payload
      );
      if (removingIndexRobot !== -1) {
        robots.splice(removingIndexRobot, 1);
      }
      state.robots = robots;
    },

    clearRobots: (state) => {
      state.robots = [...state.robots];
    },

    setAdmin: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        localStorage.setItem("uuuuuu", "-544719056");
      } else {
        localStorage.removeItem("uuuuuu");
      }
      state.isAdmin = action.payload;
    },

    setEvents: (state, action: PayloadAction<IEvent[]>) => {
      state.events = action.payload;
    },

    setSelectedEventId: (state, action: PayloadAction<number | null>) => {
      state.selectedEventId = action.payload;
    },

    setPrograms: (state, action: PayloadAction<IProgram[]>) => {
      state.programs = action.payload;
    },

    setSelectedProgramId: (state, action: PayloadAction<number | null>) => {
      state.selectedProgramId = action.payload;
    },

    setBids: (state, action: PayloadAction<IBid[]>) => {
      state.bids = action.payload;
    },

    setStages: (state, action: PayloadAction<IStage[]>) => {
      state.stages = action.payload;
    },

    setSelectedStageId: (state, action: PayloadAction<number | null>) => {
      state.selectedStageId = action.payload;
    },

    setRobotBidMap: (state, action: PayloadAction<Record<string, number>>) => {
      state.robotBidMap = action.payload;
    },
  },
});

export default receSlice.reducer;

export const {
  setRaceStatus,
  raceTime,
  raceTimeLimit,
  raceRobot,
  addPendingLaps,
  clearPendingLaps,
  removeRobot,
  clearRobots,
  setAdmin,
  setEvents,
  setSelectedEventId,
  setPrograms,
  setSelectedProgramId,
  setBids,
  setStages,
  setSelectedStageId,
  setRobotBidMap,
} = receSlice.actions;
